import { useCallback, useContext } from 'react'
import type { HTMLProps, KeyboardEvent } from 'react'
import {
  addMonths,
  addYears,
  isSameMonth,
  isSameYear,
  subMonths,
  subYears,
} from 'date-fns'
import type { DatePickerCalendarNavigationEvent } from './DatePickerCalendar'
import { clsx } from 'clsx'
import Button from '../Button'
import { useTranslation } from '../../shared'
import DatePickerContext from './DatePickerContext'
import type { InternalLocale } from '../../shared/Context'
import { formatDate } from '../date-format/DateFormatUtils'

type CalendarNavigationDateType = 'month' | 'year'
type CalendarNavigationType = 'both' | CalendarNavigationDateType

type DatePickerCalendarNavigationProps = Omit<
  HTMLProps<HTMLElement>,
  'onSelect' | 'onChange'
> & {
  type: CalendarNavigationType
  id: string
  nr?: number
  /**
   * To display what month should be shown in the first calendar by default. Defaults to the `date` respective `startDate`.
   */
  date?: Date

  /**
   * To define the locale used in the calendar. Needs to be an `date-fns` locale object, like `import { enGB } from 'date-fns/locale/en-GB'`. Defaults to `nb-NO`.
   */
  locale?: InternalLocale
}

type TitleFormatMap = {
  [T in CalendarNavigationType]: Intl.DateTimeFormatOptions
}

const titleFormats: TitleFormatMap = {
  both: {
    month: 'long',
    year: 'numeric',
  },
  month: { month: 'long' },
  year: { year: 'numeric' },
}

type CalendarDateHandlers = {
  [D in CalendarNavigationDateType]: {
    [B in CalendarNavButtonType]: typeof subMonths
  }
}

const dateHandlers: CalendarDateHandlers = {
  month: {
    prev: subMonths,
    next: addMonths,
  },
  year: {
    prev: subYears,
    next: addYears,
  },
}

export function DatePickerCalendarNav({
  type = 'both',
  id,
  nr,
  date,
  locale,
}: DatePickerCalendarNavigationProps) {
  const {
    minDate,
    maxDate,
    views,
    setViews,
    props: { link: isLinkedCalendars },
  } = useContext(DatePickerContext)
  const { selectedMonth, selectedYear } = useTranslation().DatePicker

  const title = type === 'year' ? selectedYear : selectedMonth
  const titleFormat = titleFormats[type]
  const buttonDateType = type === 'year' ? 'year' : 'month'

  const onNav = useCallback(
    ({ nr, type: navigationType }: DatePickerCalendarNavigationEvent) => {
      const handlerType = type === 'year' ? 'year' : 'month'
      const updatedViews = views.map((view) => {
        if (view.nr === nr || isLinkedCalendars) {
          const month = dateHandlers[handlerType][navigationType](
            view.month,
            1
          )

          return { ...view, month }
        }

        return view
      })
      setViews(updatedViews)
    },
    [type, views, setViews, isLinkedCalendars]
  )

  return (
    <div
      className={clsx(
        'dnb-date-picker__header__row',
        type === 'year' && 'dnb-date-picker__header__row--year'
      )}
    >
      <div className="dnb-date-picker__header__nav">
        <CalendarNavButton
          type="prev"
          nr={nr}
          date={date}
          dateLimit={minDate}
          dateType={buttonDateType}
          dateFormat={titleFormat}
          locale={locale}
          onClick={onNav}
        />
      </div>
      <span
        id={`${id}--title`}
        className="dnb-date-picker__header__title dnb-no-focus"
        title={title.replace(
          /%s/,
          formatDate(date, { locale, options: titleFormat })
        )}
        tabIndex={-1}
      >
        {formatDate(date, { locale, options: titleFormat })}
      </span>
      <div className="dnb-date-picker__header__nav">
        <CalendarNavButton
          type="next"
          dateType={buttonDateType}
          nr={nr}
          date={date}
          dateLimit={maxDate}
          dateFormat={titleFormat}
          locale={locale}
          onClick={onNav}
        />
      </div>
    </div>
  )
}

type CalendarNavButtonType = 'prev' | 'next'

export type CalendarNavButtonProps = {
  dateType: CalendarNavigationDateType
  type: CalendarNavButtonType
  nr: number
  dateFormat: Intl.DateTimeFormatOptions
  date: Date
  dateLimit: Date
  locale: InternalLocale
  onClick: ({
    nr,
    type,
  }: {
    nr: number
    type: CalendarNavButtonProps['type']
  }) => void
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void
}

type NavButtonDisabledHandlerMap = {
  [_T in CalendarNavigationDateType]: typeof isSameMonth
}

const navButtonDisabledHandlers: NavButtonDisabledHandlerMap = {
  month: isSameMonth,
  year: isSameYear,
}

function CalendarNavButton({
  type,
  dateType,
  dateFormat,
  nr,
  date,
  dateLimit,
  locale,
  onClick,
  onKeyDown,
}: CalendarNavButtonProps) {
  const translations = useTranslation().DatePicker

  const translationKey = `${type}${capitalizeFirstLetter(dateType)}`
  const dateHandler = dateHandlers[dateType][type]

  const title = translations[translationKey].replace(
    /%s/,
    formatDate(dateHandler(date, 1), {
      locale,
      options: dateFormat,
    })
  )

  const disabled =
    dateLimit && navButtonDisabledHandlers[dateType](date, dateLimit)

  const icon = type === 'prev' ? 'chevron_left' : 'chevron_right'

  return (
    <Button
      className={clsx(`dnb-date-picker__${type}`, { disabled })}
      icon={icon}
      size="small"
      aria-label={title}
      onClick={() => onClick && !disabled && onClick({ nr, type })}
      onKeyDown={onKeyDown}
    />
  )
}

function capitalizeFirstLetter(value: string) {
  return `${value.charAt(0).toLocaleUpperCase()}${value.slice(1)}`
}
